<template>
  <v-input
    :label="label"
    :rules="innerRules"
    v-model="valorInterno"
    class="InputMoney v-text-field"
    :class="{focused, dirty, alignRight}"
    :style="cssStyle"
    :required="required"
    :disabled="disabled"
    :hint="hint"
    :persistent-hint="persistentHint"
    :dense="dense"
    :single-line="singleLine"
    :hide-details="hideDetails"
    :loading="loading"
    :append-icon="appendIcon"
    @click:append="clickAppendIcon"
  >
    <money
      v-model="valorInterno"
      v-bind="money"
      :id="id"
      :disabled="disabled"
      @input="onInput"
      @focus.native="onFocus"
      @blur.native="onBlur"
      @keyup.enter.native="onChange">
    </money>
  </v-input>
</template>
<script>
import { Money } from 'v-money';
import { convertToMoney } from '@/common/functions/helpers';

export default {
  name: 'InputMoney',
  props: {
    clickAppendIcon: {
      type: Function,
      default() { return () => ({}); },
    },
    value: {
      type: [Number, String],
      default: '',
    },
    cssStyle: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: String,
    id: {
      type: String,
      default: () => `InputMoney-${Date.now().toString()}`,
    },
    rules: {
      type: Array,
      default: () => ([]),
    },
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: false,
    },
    alignRight: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    singleLine: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
      default: 'R$ ',
    },
    precision: {
      type: Number,
      default: 2,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    max: Number,
    min: Number,
    appendIcon: {
      type: String,
      default: '',
    },
  },
  components: {
    Money,
  },
  watch: {
    value: {
      immediate: true,
      handler(alterado) {
        this.valorInterno = alterado;
      },
    },
  },
  computed: {
    innerRules() {
      const rules = [...this.rules];
      if (this.required) {
        rules.push((value) => (!!value || value === 0 || value === '0') || this.$t('message.campo_obrigatorio'));
      }
      if (this.min || this.min === 0) {
        rules.push((value) => (!value || value >= this.min) || this.$t('errors.valor.minimo', { min: this.convertToMoney(this.min) }));
      }
      if (this.max || this.max === 0) {
        rules.push((value) => (!value || value <= this.max) || this.$t('errors.valor.maximo', { max: this.convertToMoney(this.max) }));
      } else {
        rules.push((value) => (!value || value <= 9999999999.99) || this.$t('errors.valor.maximo', { max: this.convertToMoney(9999999999.99) }));
      }
      return rules;
    },
  },
  data() {
    return {
      dirty: false,
      focused: false,
      valorInterno: null,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: this.prefix,
        precision: this.precision,
        masked: false,
      },
    };
  },
  methods: {
    convertToMoney,
    onInput(valor) {
      if (valor === this.value) return;
      this.dirty = true;
      this.$emit('input', valor);
      this.$emit('InputMoney_valorAlterado', valor);
    },
    onChange() {
      this.$emit('change', this.valorInterno);
    },
    onFocus() {
      this.focused = true;
    },
    onBlur() {
      this.focused = false;
      this.onChange();
      this.$emit('blur');
    },
    refresh() {
      this.$forceUpdate();
    },
  },
};
</script>
<style>
.InputMoney.v-input--is-disabled .v-input__slot:before {
  border-image: repeating-linear-gradient(90deg,rgba(0,0,0,.38) 0,rgba(0,0,0,.38) 2px,transparent 0,transparent 4px) 1 repeat;
}
.InputMoney:not(.dirty).error--text .v-input__slot:before {
  border-color: rgba(0,0,0,.42);
}
.InputMoney:not(.dirty).error--text input {
  caret-color: rgba(0,0,0,.42);
}
.InputMoney.dirty.error--text .v-input__slot:before {
  border-color: #b71c1c;
}
.InputMoney.dirty.error--text input {
  caret-color: #b71c1c;
}
.InputMoney:not(.focused){
  color: rgba(0,0,0,.42) !important;
}
.InputMoney .v-input__slot {
  transition: background .3s cubic-bezier(.25,.8,.5,1);
}
.InputMoney .v-input__slot:before {
  border-color: currentColor;
  border-style: solid;
  border-width: thin 0 0;
}
.InputMoney.error--text:not(.dirty) .v-input__slot:after {
  border-color: rgba(0,0,0,.42) !important;
}
.InputMoney .v-input__slot:after {
  border-color: currentColor;
}
.InputMoney .v-input__slot:after {
  border-style: solid;
  border-width: thin 0;
  transform: scaleX(0);
}
.InputMoney .v-input__slot:before,
.InputMoney .v-input__slot:after {
  bottom: -1px;
  content: "";
  left: 0;
  position: absolute;
  transition: .3s cubic-bezier(.25,.8,.5,1);
  width: 100%;
}
.InputMoney.focused .v-input__slot:after {
  transform: scaleX(1);
}
.InputMoney:not(.dirty) .v-input__slot .v-label {
  color: rgba(0,0,0,.54) !important;
}
.InputMoney .v-input__slot .v-label {
  position: absolute !important;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  top: 6px;
  transform-origin: top left;
  white-space: nowrap;
}
.InputMoney .v-input__slot .v-label {
  max-width: 100%;
  transform: translateY(-18px) scale(.75);
}
.InputMoney .v-input__slot .v-label:not(.error--text) {
  color: currentColor !important;
}
.InputMoney.v-input input:invalid,
.InputMoney.v-input input:required {
  box-shadow: none;
}
.InputMoney.v-input input {
  flex: 1 1 auto;
  line-height: 20px;
  padding: 8px 0;
  max-width: 100%;
  width: 100%;
}
.InputMoney.alignRight.v-input input {
  text-align: right;
}
</style>
